/**
 * Enhanced HTTP Common with Global Error Handling
 * Provides axios instances with global error interceptors and retry logic
 * 
 * Features:
 * - Global request/response interceptors
 * - Automatic error handling and reporting
 * - Network error detection and handling
 * - Retry logic for failed requests
 * - Authentication error handling
 * - Request/response logging for debugging
 */

import axios from 'axios';
import { notification } from 'antd';

// Create error handler function that will be set by the app
let globalErrorHandler = null;

// Function to set the global error handler
export const setGlobalErrorHandler = (handler) => {
  globalErrorHandler = handler;
};

// Create axios instance for main API
const httpCommon = axios.create({
  baseURL: process.env.REACT_APP_HTTPCOMMON,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Create axios instance for reports
const httpReports = axios.create({
  baseURL: process.env.REACT_APP_HTTPREPORTS,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Request interceptor for logging and authentication
const requestInterceptor = (config) => {
  // Add timestamp to track request duration
  config.metadata = { startTime: new Date() };

  return config;
};

// Request error interceptor
const requestErrorInterceptor = (error) => {
  console.error('[HTTP] Request Error:', error);
  
  if (globalErrorHandler) {
    globalErrorHandler(error, 'NETWORK', {
      showModal: false,
      showNotification: true,
      retryable: false,
    });
  }

  return Promise.reject(error);
};

// Response interceptor for logging and error handling
const responseInterceptor = (response) => {
  return response;
};

// Response error interceptor with comprehensive error handling
const responseErrorInterceptor = async (error) => {
  const originalRequest = error.config;

  // Log error details
  console.error('[HTTP] Response Error:', {
    message: error.message,
    status: error.response?.status,
    data: error.response?.data,
    url: originalRequest?.url,
  });

  // Handle different types of errors
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        // Bad Request - validation errors
        if (globalErrorHandler) {
          globalErrorHandler(error, 'VALIDATION', {
            showModal: false,
            showNotification: true,
            retryable: false,
          });
        }
        break;

      case 401:
        // Unauthorized - authentication error
        if (globalErrorHandler) {
          globalErrorHandler(error, 'AUTHENTICATION', {
            showModal: true,
            showNotification: true,
            retryable: false,
            sendEmail: true, // Send email for authentication errors
            metadata: {
              endpoint: originalRequest?.url,
              method: originalRequest?.method,
              responseData: data,
            },
          });
        }
        // Optionally clear local storage and redirect to login
        // localStorage.clear();
        // window.location.href = '/login';
        break;

      case 403:
        // Forbidden - authorization error
        if (globalErrorHandler) {
          globalErrorHandler(error, 'AUTHORIZATION', {
            showModal: true,
            showNotification: true,
            retryable: false,
            sendEmail: true, // Send email for authorization errors
            metadata: {
              endpoint: originalRequest?.url,
              method: originalRequest?.method,
              responseData: data,
            },
          });
        }
        break;

      case 404:
        // Not Found
        if (globalErrorHandler) {
          globalErrorHandler(error, 'NOT_FOUND', {
            showModal: false,
            showNotification: true,
            retryable: false,
          });
        }
        break;

      case 408:
      case 429:
        // Request Timeout or Too Many Requests - retryable
        if (globalErrorHandler) {
          globalErrorHandler(error, 'NETWORK', {
            showModal: false,
            showNotification: true,
            retryable: true,
            onRetry: () => retryRequest(originalRequest),
          });
        }
        break;

      case 500:
      case 502:
      case 503:
      case 504:
        // Server errors - retryable
        if (globalErrorHandler) {
          globalErrorHandler(error, 'SERVER', {
            showModal: status >= 503, // Show modal for service unavailable
            showNotification: true,
            retryable: true,
            sendEmail: true, // Send email for server errors
            onRetry: () => retryRequest(originalRequest),
            metadata: {
              endpoint: originalRequest?.url,
              method: originalRequest?.method,
              responseData: data,
              statusCode: status,
            },
          });
        }
        break;

      default:
        // Other server errors
        if (globalErrorHandler) {
          globalErrorHandler(error, 'SERVER', {
            showModal: false,
            showNotification: true,
            retryable: status >= 500,
            sendEmail: status >= 500, // Send email for server errors
            onRetry: status >= 500 ? () => retryRequest(originalRequest) : null,
            metadata: {
              endpoint: originalRequest?.url,
              method: originalRequest?.method,
              responseData: data,
              statusCode: status,
            },
          });
        }
    }
  } else if (error.request) {
    // Request made but no response received - network error
    if (globalErrorHandler) {
      globalErrorHandler(error, 'NETWORK', {
        showModal: true,
        showNotification: true,
        retryable: true,
        sendEmail: true, // Send email for network errors
        onRetry: () => retryRequest(originalRequest),
        metadata: {
          endpoint: originalRequest?.url,
          method: originalRequest?.method,
          networkStatus: navigator.onLine ? 'online' : 'offline',
        },
      });
    }
  } else {
    // Something else happened
    if (globalErrorHandler) {
      globalErrorHandler(error, 'APPLICATION', {
        showModal: true,
        showNotification: true,
        retryable: false,
        sendEmail: true, // Send email for application errors
        metadata: {
          endpoint: originalRequest?.url,
          method: originalRequest?.method,
          errorType: 'unknown',
        },
      });
    }
  }

  return Promise.reject(error);
};

// Retry function for failed requests
const retryRequest = async (originalRequest) => {
  try {
    // Add retry metadata
    originalRequest.metadata = {
      ...originalRequest.metadata,
      retryAttempt: (originalRequest.metadata?.retryAttempt || 0) + 1,
      retryTime: new Date(),
    };

    // Determine which axios instance to use
    const axiosInstance = originalRequest.baseURL === process.env.REACT_APP_HTTPREPORTS 
      ? httpReports 
      : httpCommon;

    const response = await axiosInstance(originalRequest);
    return response;
  } catch (retryError) {
    throw retryError;
  }
};

// Add interceptors to both instances
[httpCommon, httpReports].forEach(instance => {
  // Request interceptors
  instance.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
  
  // Response interceptors
  instance.interceptors.response.use(responseInterceptor, responseErrorInterceptor);
});

// Network connectivity check
export const checkNetworkConnectivity = async () => {
  try {
    await httpCommon.get('/health', { timeout: 5000 });
    return true;
  } catch (error) {
    return false;
  }
};

// Helper function to determine if error is retryable
export const isRetryableError = (error) => {
  if (!error.response) {
    // Network errors are generally retryable
    return true;
  }

  const { status } = error.response;
  // Retry for server errors and rate limiting
  return status >= 500 || status === 408 || status === 429;
};

// Helper function to get user-friendly error message
export const getErrorMessage = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error.response?.data?.error) {
    return error.response.data.error;
  }

  if (error.message) {
    return error.message;
  }

  return 'An unexpected error occurred';
};

// Cancel token for request cancellation
export const CancelToken = axios.CancelToken;
export const isCancel = axios.isCancel;

// Create cancel token source
export const createCancelTokenSource = () => {
  return CancelToken.source();
};

export { httpCommon as default, httpReports };